/**
 * useQueuedMutations — exposes the IndexedDB-backed mutation queue
 * count to the UI. Polls every 5 seconds while mounted (cheap: a
 * single IDB count read), and refreshes immediately whenever the
 * online state flips so the badge zeros out as soon as a flush
 * completes.
 */

import { useEffect, useRef, useState } from 'react'
import { getClientQueue }              from '@/lib/sync/flush'

const POLL_MS = 5_000

export function useQueuedMutations(): { count: number; refresh: () => void } {
  const [count, setCount] = useState(0)
  // Hold a ref to the latest tick fn so the cleanup effect can cancel.
  const cancelledRef = useRef(false)

  const refresh = (): void => {
    void readCount().then((c) => {
      if (!cancelledRef.current) setCount(c)
    })
  }

  useEffect(() => {
    cancelledRef.current = false
    let timer: number | undefined

    const tick = async (): Promise<void> => {
      if (cancelledRef.current) return
      const c = await readCount()
      if (cancelledRef.current) return
      setCount(c)
      timer = window.setTimeout(tick, POLL_MS)
    }
    void tick()

    return (): void => {
      cancelledRef.current = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [])

  return { count, refresh }
}

async function readCount(): Promise<number> {
  try {
    return await getClientQueue().pendingCount()
  } catch {
    return 0
  }
}
